import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiSave, 
  FiX, 
  FiAlertCircle,
  FiPackage,
  FiDollarSign,
  FiUsers,
  FiHeart,
  FiShoppingBag,
  FiBookOpen,
  FiSearch
} from 'react-icons/fi';
import api from '../../services/api';
import logger from '../../utils/logger';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    padding: 0 0.75rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: clamp(1.5rem, 2.5vw + 1rem, 1.875rem);
  font-weight: 700;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.375rem;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 0.875rem;
  background-color: var(--background-primary);
  transition: var(--transition);
  min-height: 44px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(26, 54, 93, 0.1);
  }
  
  @media (max-width: 768px) {
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
`;

const AddButton = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: 0;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 44px;
  touch-action: manipulation;

  &:hover {
    background: var(--primary-hover);
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 1rem;
    font-size: 0.875rem;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background-color: ${props => props.active ? 'var(--primary-color)' : 'var(--background-primary)'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.875rem;
  
  &:hover {
    background-color: ${props => props.active ? 'var(--primary-light)' : 'var(--background-secondary)'};
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-color);
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CardInfo = styled.div`
  flex: 1;
`;

const ProductName = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProductType = styled.span`
  background: var(--primary-color-light);
  color: var(--primary-color);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const Description = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0.5rem 0 1rem 0;
  line-height: 1.4;
`;

const PriceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Price = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'edit' ? 'var(--primary-color)' : props.variant === 'delete' ? 'var(--error-color)' : 'var(--background-secondary)'};
  color: ${props => props.variant === 'delete' ? 'white' : 'var(--text-primary)'};
  border: none;
  border-radius: 0;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.variant === 'edit' ? 'var(--primary-hover)' : props.variant === 'delete' ? 'var(--error-dark)' : 'var(--border-color)'};
    transform: translateY(-1px);
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.9rem;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const StatusBadge = styled.span`
  background: ${props => props.active ? 'var(--success-color)' : 'var(--error-color)'};
  color: var(--text-inverse);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const FeaturedBadge = styled.span`
  background: var(--warning-color);
  color: var(--text-inverse);
  padding: 0.25rem 0.75rem;
  border-radius: 0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);
  
  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  h3 {
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(216, 184, 137, 0.1);
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' && `
    background: var(--primary-color);
    color: var(--text-inverse);
    border: none;

    &:hover {
      background: var(--primary-hover);
      transform: translateY(-1px);
    }
  `}

  ${props => props.variant === 'secondary' && `
    background: var(--background-secondary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);

    &:hover {
      background: var(--border-color);
    }
  `}
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  background: rgba(252, 129, 129, 0.1);
  color: var(--error-color);
  padding: 1rem;
  border-radius: 0;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const getProductTypeIcon = (type) => {
  switch (type) {
    case 'curriculum_package':
      return <FiBookOpen size={20} />;
    case 'student_sponsorship':
      return <FiUsers size={20} />;
    case 'donation':
      return <FiHeart size={20} />;
    case 'merchandise':
      return <FiShoppingBag size={20} />;
    default:
      return <FiPackage size={20} />;
  }
};

const getProductTypeLabel = (type) => {
  switch (type) {
    case 'curriculum_package':
      return 'Curriculum Package';
    case 'student_sponsorship':
      return 'Student Sponsorship';
    case 'donation':
      return 'Donation';
    case 'merchandise':
      return 'Merchandise';
    default:
      return 'Other';
  }
};

function AdditionalProductsManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_type: 'curriculum_package',
    price: '',
    currency: 'USD',
    is_featured: false,
    image_url: '',
    max_quantity: '',
    available_quantity: '',
    start_date: '',
    end_date: '',
    is_active: true,
    categories: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      logger.debug('Fetching additional products data...');
      
      const productsResponse = await api.getAdditionalProducts();
      
      logger.debug('Products response', { productsCount: productsResponse.products?.length || 0 });
      
      setProducts(productsResponse.products || []);
    } catch (error) {
      logger.error('Error fetching data', { error });
      setError(`Failed to load data: ${error.message || error}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         getProductTypeLabel(product.product_type).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = activeFilter === 'all' || product.product_type === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      product_type: 'curriculum_package',
      price: '',
      currency: 'USD',
      is_featured: false,
      image_url: '',
      max_quantity: '',
      available_quantity: '',
      start_date: '',
      end_date: '',
      is_active: true,
      categories: []
    });
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      product_type: product.product_type,
      price: product.price,
      currency: product.currency,
      is_featured: product.is_featured,
      image_url: product.image_url || '',
      max_quantity: product.max_quantity || '',
      available_quantity: product.available_quantity || '',
      start_date: product.start_date ? product.start_date.split('T')[0] : '',
      end_date: product.end_date ? product.end_date.split('T')[0] : '',
      is_active: product.is_active,
      categories: product.categories || []
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.deleteAdditionalProduct(id);
        await fetchData();
      } catch (error) {
        logger.error('Error deleting product', { error, productId: id });
        setError('Failed to delete product');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    logger.debug('Form submitted', { formData, isEditing: !!editingProduct });
    
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        max_quantity: formData.max_quantity ? parseInt(formData.max_quantity) : null,
        available_quantity: formData.available_quantity ? parseInt(formData.available_quantity) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      logger.debug('Submitting data', { submitData, isEditing: !!editingProduct });

      if (editingProduct) {
        logger.debug('Updating product', { productId: editingProduct.id });
        await api.updateAdditionalProduct(editingProduct.id, submitData);
      } else {
        logger.debug('Creating new product');
        await api.createAdditionalProduct(submitData);
      }

      logger.debug('Product saved successfully');
      setShowModal(false);
      await fetchData();
    } catch (error) {
      logger.error('Error saving product', { error, formData, isEditing: !!editingProduct });
      setError(`Failed to save product: ${error.message || error}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No limit';
    return new Date(dateString).toLocaleDateString();
  };


  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading additional products...</LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Additional Products Management</Title>
        <SearchBar>
          <SearchIcon>
            <FiSearch size={16} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>
        <AddButton onClick={handleAddNew}>
          <FiPlus size={16} />
          Add Product
        </AddButton>
      </Header>

      {error && (
        <ErrorMessage>
          <FiAlertCircle size={16} />
          {error}
        </ErrorMessage>
      )}

      <Filters>
        <FilterButton
          active={activeFilter === 'all'}
          onClick={() => setActiveFilter('all')}
        >
          All ({products.length})
        </FilterButton>
        <FilterButton
          active={activeFilter === 'curriculum_package'}
          onClick={() => setActiveFilter('curriculum_package')}
        >
          Curriculum Packages ({products.filter(p => p.product_type === 'curriculum_package').length})
        </FilterButton>
        <FilterButton
          active={activeFilter === 'student_sponsorship'}
          onClick={() => setActiveFilter('student_sponsorship')}
        >
          Sponsorships ({products.filter(p => p.product_type === 'student_sponsorship').length})
        </FilterButton>
        <FilterButton
          active={activeFilter === 'donation'}
          onClick={() => setActiveFilter('donation')}
        >
          Donations ({products.filter(p => p.product_type === 'donation').length})
        </FilterButton>
        <FilterButton
          active={activeFilter === 'merchandise'}
          onClick={() => setActiveFilter('merchandise')}
        >
          Merchandise ({products.filter(p => p.product_type === 'merchandise').length})
        </FilterButton>
      </Filters>

      {filteredProducts.length > 0 ? (
        <ProductsGrid>
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <CardHeader>
                <CardInfo>
                  <ProductName>
                    {getProductTypeIcon(product.product_type)}
                    {product.name}
                    {product.is_featured && <FeaturedBadge>Featured</FeaturedBadge>}
                  </ProductName>
                  <ProductType>{getProductTypeLabel(product.product_type)}</ProductType>
                  {product.description && <Description>{product.description}</Description>}
                  <PriceInfo>
                    <FiDollarSign size={16} color="var(--primary-color)" />
                    <Price>{product.price} {product.currency}</Price>
                  </PriceInfo>
                </CardInfo>
                <CardActions>
                  <ActionButton variant="edit" onClick={() => handleEdit(product)}>
                    <FiEdit size={16} />
                  </ActionButton>
                  <ActionButton variant="delete" onClick={() => handleDelete(product.id)}>
                    <FiTrash2 size={16} />
                  </ActionButton>
                </CardActions>
              </CardHeader>
              <CardBody>
                <InfoRow>
                  <InfoLabel>Status</InfoLabel>
                  <StatusBadge active={product.is_active}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                </InfoRow>
                {product.max_quantity && (
                  <InfoRow>
                    <InfoLabel>Max Quantity</InfoLabel>
                    <InfoValue>{product.max_quantity}</InfoValue>
                  </InfoRow>
                )}
                {product.available_quantity !== null && (
                  <InfoRow>
                    <InfoLabel>Available</InfoLabel>
                    <InfoValue>{product.available_quantity}</InfoValue>
                  </InfoRow>
                )}
                <InfoRow>
                  <InfoLabel>Valid Until</InfoLabel>
                  <InfoValue>{formatDate(product.end_date)}</InfoValue>
                </InfoRow>
                {product.categories && product.categories.length > 0 && (
                  <InfoRow>
                    <InfoLabel>Categories</InfoLabel>
                    <InfoValue>{product.categories.join(', ')}</InfoValue>
                  </InfoRow>
                )}
              </CardBody>
            </ProductCard>
          ))}
        </ProductsGrid>
      ) : (
        <EmptyState>
          <FiPackage />
          <h3>No products found</h3>
          <p>Try adjusting your search or filters to find what you're looking for.</p>
        </EmptyState>
      )}

      {showModal && (
        <Modal
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <ModalHeader>
              <ModalTitle>
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Product Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Full Year Curriculum Package"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe this product..."
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Product Type *</Label>
                  <Select
                    value={formData.product_type}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    required
                  >
                    <option value="curriculum_package">Curriculum Package</option>
                    <option value="student_sponsorship">Student Sponsorship</option>
                    <option value="donation">Donation</option>
                    <option value="merchandise">Merchandise</option>
                    <option value="other">Other</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Price *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="99.99"
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Currency</Label>
                  <Select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Max Quantity (optional)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_quantity}
                    onChange={(e) => setFormData({ ...formData, max_quantity: e.target.value })}
                    placeholder="Leave empty for unlimited"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Start Date (optional)</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>End Date (optional)</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Featured Product
                </Label>
              </FormGroup>

              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Active
                </Label>
              </FormGroup>

              <ModalActions>
                <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary">
                  <FiSave size={16} />
                  {editingProduct ? 'Update' : 'Create'}
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}

export default AdditionalProductsManagement;
